import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from 'framer-motion';
import { FiSun, FiMoon, FiMenu, FiX, FiCalendar } from 'react-icons/fi';
import { trackNavigationClick, trackThemeToggle, trackSectionView, trackEvent } from '../utils/mixpanel';
import './Navigation.scss';

interface NavItem {
  label: string;
  target: string;
  type: 'scroll' | 'link';
}

const navItems: NavItem[] = [
  { label: 'About', target: 'about-me', type: 'scroll' },
  { label: 'Skills', target: 'expertise', type: 'scroll' },
  { label: 'Experience', target: 'history', type: 'scroll' },
  { label: 'Projects', target: 'projects', type: 'scroll' },
  { label: 'Contact', target: 'contact', type: 'scroll' },
  {
    label: 'Blog',
    target: process.env.NODE_ENV === 'development' ? 'http://localhost:4321/blog' : '/blog',
    type: 'link'
  },
];

// Placeholder - user will provide their Calendly URL
const CALENDLY_URL = 'https://calendly.com/your-link';

interface NavigationProps {
  parentToChild: { mode: string };
  modeChange: () => void;
}

function Navigation({ parentToChild, modeChange }: NavigationProps) {
  const { mode } = parentToChild;
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleNavClick = (item: NavItem) => {
    if (item.type === 'link') {
      trackNavigationClick(item.label.toLowerCase());
      window.location.href = item.target;
    } else {
      const element = document.getElementById(item.target);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
        trackNavigationClick(item.target);
        trackSectionView(item.target);
      }
    }
    setMobileOpen(false);
  };

  const handleMobileToggle = () => {
    setMobileOpen(prev => {
      if (!prev) trackEvent('Mobile Menu Opened', {});
      return !prev;
    });
  };

  const handleThemeToggle = () => {
    modeChange();
    trackThemeToggle(mode === 'dark' ? 'light' : 'dark');
  };

  return (
    <>
      <motion.header
        className={`navbar ${scrolled ? 'navbar--scrolled' : ''}`}
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.5, ease: 'easeOut' }}
      >
        <div className="navbar__container">
          {/* Logo */}
          <a href="/" className="navbar__logo">
            <span className="navbar__logo-text">Ameer</span>
            <span className="navbar__logo-accent">.</span>
          </a>

          {/* Desktop Navigation */}
          <nav className="navbar__nav">
            {navItems.map((item) => (
              <button
                key={item.label}
                className="navbar__link"
                onClick={() => handleNavClick(item)}
              >
                {item.label}
              </button>
            ))}
          </nav>

          {/* Actions */}
          <div className="navbar__actions">
            {/* Theme Toggle */}
            <button
              className="navbar__theme-toggle"
              onClick={handleThemeToggle}
              aria-label="Toggle theme"
            >
              {mode === 'dark' ? <FiSun /> : <FiMoon />}
            </button>

            {/* Schedule Call Button (Desktop) */}
            <a
              href={CALENDLY_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="navbar__cta"
            >
              <FiCalendar />
              <span>Schedule a Call</span>
            </a>

            {/* Mobile Menu Button */}
            <button
              className="navbar__mobile-toggle"
              onClick={handleMobileToggle}
              aria-label="Toggle menu"
            >
              {mobileOpen ? <FiX /> : <FiMenu />}
            </button>
          </div>
        </div>
      </motion.header>

      {/* Mobile Menu */}
      <AnimatePresence>
        {mobileOpen && (
          <>
            <motion.div
              className="mobile-overlay"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setMobileOpen(false)}
            />
            <motion.div
              className="mobile-menu"
              initial={{ x: '100%' }}
              animate={{ x: 0 }}
              exit={{ x: '100%' }}
              transition={{ type: 'tween', duration: 0.3 }}
            >
              <div className="mobile-menu__header">
                <span className="mobile-menu__title">Menu</span>
                <button
                  className="mobile-menu__close"
                  onClick={() => setMobileOpen(false)}
                  aria-label="Close menu"
                >
                  <FiX />
                </button>
              </div>

              <nav className="mobile-menu__nav">
                {navItems.map((item, index) => (
                  <motion.button
                    key={item.label}
                    className="mobile-menu__link"
                    onClick={() => handleNavClick(item)}
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.05 }}
                  >
                    {item.label}
                  </motion.button>
                ))}
              </nav>

              <div className="mobile-menu__footer">
                <a
                  href={CALENDLY_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mobile-menu__cta"
                >
                  <FiCalendar />
                  <span>Schedule a Call</span>
                </a>

                <button
                  className="mobile-menu__theme"
                  onClick={handleThemeToggle}
                >
                  {mode === 'dark' ? <FiSun /> : <FiMoon />}
                  <span>{mode === 'dark' ? 'Light Mode' : 'Dark Mode'}</span>
                </button>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
}

export default Navigation;
